import { createAction } from '@reduxjs/toolkit'
import { all, call, put, select, takeEvery } from 'redux-saga/effects'

import { deleteTemporaryDrawableGroups } from './deleteTemporaryDrawableGroups'
import {
    filterDrawableGroups,
    setActiveDrawable,
    setActiveDrawableGroup,
    setLocationErrorMessage,
} from '../../../actions/drawable'
import managers from '../../lib/managers'
import PaperManager from '../../lib/managers/PaperManager'
import { Flag, Select, Workspace } from '../../lib/toolBoxes/2D'
import {
    clearPendingRimboardBorders,
    clearSelectedAiSuggestionIds,
    resetNewCreatedDrawables,
    selectRimboardPendingBorderIds,
    setFlag,
    setSelectedMaterialSubType,
    unsetActiveDrawableId,
    updateSelectedPaperId,
} from '../../slices/2D'
import { updateMeshPoints } from '../../slices/3D'
import { selectActiveMode, setJoistDirection, setJoistDirectionOther } from '../../slices/common'
import { toggleFlagForm } from '../../slices/materialFlags'
import { activateTool } from '../../slices/tools'
import { DRAW_TOOL_ACTION, VIEW_MODE } from '../../types'
import { resetAIDrawables } from '../2D/resetAIDrawables'

export const closeMaterialFormAction = createAction('closeMaterialForm')

export function* handleCloseMaterialForm() {
    try {
        const activeMode = yield select(selectActiveMode)

        const manager: PaperManager | null = yield call(
            activeMode === VIEW_MODE.Markup3D ? managers.get3DManager : managers.get2DManager
        )

        if (!manager) return

        const workspaceTool: Workspace = yield call(manager.getTool, Workspace.NAME)

        yield call(deleteTemporaryDrawableGroups)

        // Remove pending rimboard borderlines from canvas if the user canceled without saving
        const rimboardPendingBorderIds: number[] | null = yield select(selectRimboardPendingBorderIds)

        if (rimboardPendingBorderIds?.length) {
            yield all(rimboardPendingBorderIds.map((id) => call(workspaceTool.removeItemWithPaperId, id)))
            yield put(clearPendingRimboardBorders())
        }

        // Delete temporary flags from canvas
        const temporaryFlagItems: paper.Item[] = yield call(workspaceTool.getItemsWithCriteria, 'data', (data) => {
            return data?.toolName === Flag.NAME && data?.isTemporaryGroup
        })

        // Remove temporary flags
        yield all(temporaryFlagItems.map((item) => item.remove()))

        yield all([
            put(unsetActiveDrawableId()),
            put(resetNewCreatedDrawables()),
            put(toggleFlagForm({ toggleForm: false })), // close the flag form when the form is closed
            put(setFlag(DRAW_TOOL_ACTION.NEW_OPENING)),
            put(setJoistDirectionOther(null)),
            put(setJoistDirection(null)),
            put(updateMeshPoints(null)),
            put(setActiveDrawableGroup(null)),
            put(setActiveDrawable(null, 0)),
            put(setLocationErrorMessage('')),
            put(resetAIDrawables()),
            put(filterDrawableGroups()),
            put(clearSelectedAiSuggestionIds()),
            put({ type: activateTool.type, payload: Select.NAME }),
            put(setSelectedMaterialSubType(null)),
            put(updateSelectedPaperId(null)),
        ])

        if (activeMode === VIEW_MODE.Markup2D) yield call(workspaceTool.lockItems, false)
    } catch (error) {
        yield call(console.error, error)
    }
}

export function* watchForCloseMaterialForm() {
    yield takeEvery(closeMaterialFormAction.type, handleCloseMaterialForm)
}
